/**
 * UserRowColumns - Reusable table cell components for user rows
 */
import React from 'react';
import { Building2 } from 'lucide-react';
import Tooltip from '../../ui/overlays/Tooltip';
import { 
  formatNumber, 
  formatTimeRemaining, 
  getDayOfWeekInCST, 
  formatDateTimeInCST,
  formatPhoneDisplay,
  getSubscriptionBadgeInfo
} from './userRowUtils';

// Name column with avatar
export function NameColumn({ user, displayName, cancellationInfo, isNewCustomer, isEnterpriseSection, getEnterpriseMetaForUser }) {
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center">
        <div className="flex-shrink-0 h-10 w-10">
          {(() => {
            if (isEnterpriseSection) {
              const meta = getEnterpriseMetaForUser?.(user.email, user);
              if (meta?.enterpriseImage) {
                return (
                  <img
                    src={meta.enterpriseImage}
                    alt={meta.enterpriseName || 'Enterprise'}
                    className="h-10 w-10 rounded-lg object-contain bg-white"
                  />
                );
              }
            }
            return (
              <div className="h-10 w-10 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {displayName.charAt(0).toUpperCase()}
                </span>
              </div>
            );
          })()}
        </div>
        <div className="ml-4">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {displayName}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            ID: {user.uid?.slice(-8) || 'N/A'}
          </div>
        </div>
        {/* Cancellation or New Badge */}
        {cancellationInfo ? (
          <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${cancellationInfo.className}`}>
            {cancellationInfo.text}
          </span>
        ) : isNewCustomer(user) && (
          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            New!
          </span>
        )}
      </div>
    </td>
  );
}

// Enterprise column
export function EnterpriseColumn({ user, getEnterpriseNameForUser, getEnterpriseMetaForUser }) {
  const enterpriseName = getEnterpriseNameForUser(user.email, user);
  if (enterpriseName) {
    const meta = getEnterpriseMetaForUser?.(user.email, user);
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-2">
          {meta?.enterpriseImage ? (
            <img src={meta.enterpriseImage} alt={enterpriseName} className="h-4 w-4 rounded object-contain bg-white" />
          ) : (
            <Building2 className="h-4 w-4 text-blue-500" />
          )}
          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
            {enterpriseName}
          </span>
        </div>
      </td>
    );
  }
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
      <span className="text-gray-400">Individual</span>
    </td>
  );
}

// Phone column
export function PhoneColumn({ phone }) {
  const phoneData = formatPhoneDisplay(phone);
  if (!phoneData) {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        <span className="text-gray-400">-</span>
      </td>
    );
  }
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
      <a
        href={`tel:${phoneData.telHref}`}
        className="text-blue-600 dark:text-blue-400 hover:underline"
        title="Click to call"
      >
        {phoneData.display}
      </a>
    </td>
  );
}

// Trial end column
export function TrialEndColumn({ user, getTrialEndDate }) {
  const trialEndDate = getTrialEndDate(user);
  if (!trialEndDate) {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        <span className="text-gray-400">N/A</span>
      </td>
    );
  }
  
  const now = new Date();
  const timeLeft = trialEndDate - now;
  
  if (timeLeft <= 0) {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        <span className="text-red-600 dark:text-red-400 font-medium">Expired</span>
      </td>
    );
  }
  
  const dayOfWeek = getDayOfWeekInCST(trialEndDate);
  const formattedDateTime = formatDateTimeInCST(trialEndDate);
  
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
      <div className="flex flex-col">
        <span className="font-medium text-gray-900 dark:text-white">
          {formatTimeRemaining(timeLeft)}
        </span>
        <span className="text-xs text-blue-600 dark:text-blue-400 mt-1">
          Ends on {dayOfWeek}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" title={formattedDateTime}>
          {formattedDateTime}
        </span>
      </div>
    </td>
  );
}

// Next decision date column
export function NextDecisionDateColumn({ decisionInfo }) {
  if (!decisionInfo) {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
        <span className="text-red-500 font-bold">DEBUG: NO DECISION INFO</span>
      </td>
    );
  }

  let colorClass = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  if (decisionInfo.isOverdue) {
    colorClass = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  } else if (decisionInfo.totalMinutes < 1440) { // Less than 24 hours
    colorClass = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  } else if (decisionInfo.totalMinutes < 4320) { // Less than 3 days
    colorClass = 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
  } else if (decisionInfo.totalMinutes < 10080) { // Less than 7 days
    colorClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
  }
  
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
      <div className="flex flex-col">
        <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${colorClass}`}>
          {decisionInfo.text}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {decisionInfo.type} • {decisionInfo.date}
        </span>
      </div>
    </td>
  );
}

// Member since column
export function MemberSinceColumn({ user, formatDate, formatExactDateTime, timeAgo }) {
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
      <Tooltip content={formatExactDateTime(user.createdAt)} id={`member-since-${user.uid}`}>
        <div className="cursor-help">
          <div>{formatDate(user.createdAt)}</div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {timeAgo(user.createdAt)}
          </div>
        </div>
      </Tooltip>
    </td>
  );
}

// Last active column
export function LastActiveColumn({ lastActive }) {
  if (!lastActive) {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        <span className="text-gray-400">Never</span>
      </td>
    );
  }
  
  const lastActiveDate = new Date(lastActive);
  const now = new Date();
  const diffInMinutes = Math.floor((now - lastActiveDate) / (1000 * 60));
  
  let content;
  if (diffInMinutes < 10) {
    content = <span className="text-green-600 dark:text-green-400 font-medium">Active now</span>;
  } else if (diffInMinutes < 60) {
    content = <span className="text-blue-600 dark:text-blue-400">{diffInMinutes} min ago</span>;
  } else if (diffInMinutes < 1440) { // Less than 24 hours
    const hours = Math.floor(diffInMinutes / 60);
    content = <span className="text-yellow-600 dark:text-yellow-400">{hours} hour{hours !== 1 ? 's' : ''} ago</span>;
  } else {
    const days = Math.floor(diffInMinutes / 1440);
    content = <span className="text-gray-600 dark:text-gray-400">{days} day{days !== 1 ? 's' : ''} ago</span>;
  }
  
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
      {content}
    </td>
  );
}

// Subscription column
export function SubscriptionColumn({ user }) {
  const badgeInfo = getSubscriptionBadgeInfo(user);
  
  if (badgeInfo.text === 'Basic') {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
        <span className="text-gray-400">Basic</span>
      </td>
    );
  }
  
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
      <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${badgeInfo.className}`}>
        {badgeInfo.text}
      </span>
    </td>
  );
}

// Metric badge column (for various numeric metrics)
export function MetricBadgeColumn({ value, colorClass }) {
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
      <span className={`inline-flex items-center px-2 py-1 rounded-lg text-sm font-medium ${colorClass}`}>
        {formatNumber(value || 0)}
      </span>
    </td>
  );
}

// Engagement column
export function EngagementColumn({ currentStreak, activeDaysThisMonth }) {
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
      <div className="flex flex-col items-center space-y-1">
        <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
          🔥 {formatNumber(currentStreak || 0)} streak
        </span>
        <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
          📅 {formatNumber(activeDaysThisMonth || 0)}/mo
        </span>
      </div>
    </td>
  );
}

// Revenue column
export function RevenueColumn({ amount, isMonthly = false }) {
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
      <span className={`inline-flex items-center px-2 py-1 rounded-lg text-sm font-medium ${isMonthly ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'}`}>
        ${formatNumber(amount || 0)}
      </span>
    </td>
  );
}

// Months as customer column
export function CustomerTenureColumn({ months }) {
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
      <span className="inline-flex items-center px-2 py-1 rounded-lg text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
        {months ? `${months}mo` : '0mo'}
      </span>
    </td>
  );
}

// Free user column
export function FreeUserColumn({ isFreeUser }) {
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
      {isFreeUser ? (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
          Free
        </span>
      ) : (
        <span className="text-gray-400">No</span>
      )}
    </td>
  );
}

